import React from 'react';
import PanelHeader from './PanelHeader';
import { useFecharNoEsc } from './useFecharNoEsc';
import { useMudouNoCorpo } from './useMudouNoCorpo';
import { useVoltarFecha } from './useVoltarFecha';
import PerguntaDeFechar from './PerguntaDeFechar';

/**
 * A CASCA dos modais de ficha e de edição: cabeçalho, abas, faixa de números,
 * corpo rolante e rodapé. Cada tela entra só com o conteúdo.
 * Prévia: https://claude.ai/artifact/LGx85pETYBgDmYz8zmmaFP ("Uma casca só").
 *
 * ⛔ LAYOUT NUM LUGAR SÓ. Até 03/10/2026 cada modal carregava a própria conta de
 * altura, e foi assim que o modal da fatura cortou os botões no zoom de 90% (dois
 * max-h chumbados). Aqui a caixa é flex-col overflow-hidden, o corpo é
 * flex-1 min-h-0 overflow-y-auto e o resto é shrink-0: o corpo recebe o que sobrar.
 *
 * ⚠️ DOIS MODOS DE ALTURA, e os dois são decisão:
 * - `maxima` (padrão): a caixa cresce com o conteúdo até 90vh. Fichas de leitura.
 * - `fixa`: 85vh sempre. O Editar Fatura usa porque "Dados Gerais" e "Itens e
 *   Consumo" têm tamanhos muito diferentes e a caixa saltava a cada troca de aba.
 * - `tela`: a tela inteira menos a margem. O Editar Assinatura, com a grade larga
 *   de itens e quatro abas de tamanhos diferentes.
 *
 * Cor da casca (09/10/2026): os central-* da Central, como a moldura. O CONTEÚDO de quem usa não é da casca.
 *
 * Acento: neutro na aba ativa. Cor fica para VALOR (verde/vermelho) e laranja
 * só na ação que grava, que vem de quem usa a casca.
 */
export interface NumeroDaFaixa {
  k: string;
  v: React.ReactNode;
  /** Classe de cor do valor (só para valor: verde, vermelho, âmbar). */
  cls?: string;
  dica?: string;
  /** Linha miúda sob o valor (a data por trás de um "3d"). */
  sub?: React.ReactNode;
}

export interface AbaDaCasca {
  chave: string;
  rotulo: React.ReactNode;
}

interface Props {
  titulo: React.ReactNode;
  /** Título em várias linhas em vez de cortado (ver `quebrarTitulo` do PanelHeader). */
  quebrarTitulo?: boolean;
  /** Linha mono de identificação: código · razão social · CNPJ. */
  subtitulo?: React.ReactNode;
  selos?: React.ReactNode;
  onClose: () => void;
  /** Controle da ficha inteira, logo abaixo do cabeçalho e acima do aviso e das
   *  abas: a barra do funil do lead, passos de um assistente. NUNCA alerta (isso
   *  é `aviso`), e nunca conteúdo de uma aba só. */
  subCabecalho?: React.ReactNode;
  /** Avisos que valem para todas as abas (ex.: o que já saiu para o mundo). */
  aviso?: React.ReactNode;
  abas?: AbaDaCasca[];
  abaAtiva?: string;
  onAba?: (chave: string) => void;
  faixa?: NumeroDaFaixa[];
  /** Entre a faixa e o corpo, fora da rolagem (ex.: a frase do rateio). */
  topo?: React.ReactNode;
  children: React.ReactNode;
  rodape?: React.ReactNode;
  altura?: 'maxima' | 'fixa' | 'tela';
  /** `xs`/`sm`/`base` são os diálogos de ação (enviar, silenciar, baixar); `md` em
   *  diante, fichas e editores. */
  largura?: 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl';
  /** Padding do corpo; o editor usa mais folga que as fichas. */
  corpoClassName?: string;
  zIndex?: string;
  /** Clique no fundo escuro fecha. Os diálogos de ação migrados já faziam isso, e a
   *  migração não pode tirar (paridade). Editor fica em `false`: um clique fora não
   *  pode jogar fora o que foi digitado. */
  fecharAoClicarFora?: boolean;
  /** A caixa inteira vira zona de soltar arquivo (padrão do CLAUDE.md: o
   *  DemandasTab usa o modal todo como zona). A casca faz o preventDefault do
   *  dragover; quem usa recebe o drop e processa pelo MESMO handler do input. */
  aoSoltarArquivos?: (e: React.DragEvent<HTMLDivElement>) => void;
  /** `false` = operação em curso que não pode ser abandonada pela moldura (progresso
   *  de lote): sem ×, clique fora não fecha e Esc não fecha. A saída é o botão que
   *  quem usa põe no rodapé quando a operação termina. Padrão `true`. */
  fechavel?: boolean;
  /** Nome do diálogo para leitor de tela quando o título não é texto puro. */
  rotulo?: string;
  /** Formulário: há algo digitado que o fechar perderia? Decide o Esc (09/10/2026): `false` fecha direto; `true` ou
   *  AUSENTE pergunta "Fechar sem salvar?" antes. Ausente pergunta porque a casca não sabe: perguntar é mais barato que
   *  perder o digitado. Não vale para a casca que já fecha no clique fora (leitura), nem para a não `fechavel`. */
  temEdicaoNaoSalva?: boolean | (() => boolean);
  /** A URL enquanto a casca está aberta (ex. "/clientes?cliente=5"): o link copiado e o F5 reabrem. Ver useVoltarFecha. */
  endereco?: string;
}

const LARGURA = {
  xs: 'max-w-md', sm: 'max-w-lg', base: 'max-w-2xl',
  md: 'max-w-3xl', lg: 'max-w-4xl', xl: 'max-w-5xl', '2xl': 'max-w-7xl',
};
const ALTURA = { maxima: 'max-h-[90vh]', fixa: 'h-[85vh]', tela: 'h-[calc(100vh-2rem)]' };

export default function CascaDeModal({
  titulo, quebrarTitulo, subtitulo, selos, onClose, subCabecalho, aviso, abas, abaAtiva, onAba, faixa, topo, children, rodape,
  altura = 'maxima', largura = 'lg', corpoClassName = 'px-5 py-3', zIndex = 'z-50',
  fecharAoClicarFora = false, aoSoltarArquivos, fechavel = true, rotulo, temEdicaoNaoSalva, endereco,
}: Props) {
  const fechaNoFundo = fecharAoClicarFora && fechavel;
  const [perguntando, setPerguntando] = React.useState(false);
  // O Esc (09/10/2026, opção (b) do Maestro): leitura fecha, como o clique fora; formulário fecha direto só quando a
  // tela diz que não há nada digitado, e senão PERGUNTA; operação em curso (não `fechavel`) engole.
  const { corpoRef, mudou: mudouNoCorpo } = useMudouNoCorpo();
  const aoSair = !fechavel ? null : fechaNoFundo ? onClose : () => {
    // Sem a tela dizer, a casca olha o corpo contra o estado em que abriu (useMudouNoCorpo, 16/10/2026).
    const sujo = temEdicaoNaoSalva === undefined ? mudouNoCorpo() : typeof temEdicaoNaoSalva === 'function' ? temEdicaoNaoSalva() : temEdicaoNaoSalva;
    if (sujo === false) onClose(); else setPerguntando(true);
  };
  useFecharNoEsc(aoSair);
  // O Voltar do navegador faz o MESMO que o Esc (14/10/2026, opção (a) do Heitor): fecha a casca antes de trocar de tela.
  useVoltarFecha(aoSair, endereco);
  return (
    <div
      className={`fixed inset-0 bg-black/70 flex items-center justify-center p-4 ${zIndex}`}
      // Fecha no MOUSEDOWN com alvo conferido, como a CascaDeGaveta. No click, um
      // arrasto que começa dentro da caixa (selecionar texto num campo) e termina
      // fora dispara o click no fundo e fechava o modal com o formulário junto.
      onMouseDown={fechaNoFundo ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
      data-testid='casca-fundo'
    >
      <div
        className={`bg-central-cartao rounded-xl shadow-2xl w-full ${LARGURA[largura]} ${ALTURA[altura]} flex flex-col overflow-hidden border border-central-borda`}
        role='dialog'
        aria-modal='true'
        aria-label={rotulo || (typeof titulo === 'string' ? titulo : undefined)}
        onDragOver={aoSoltarArquivos ? (e) => e.preventDefault() : undefined}
        onDrop={aoSoltarArquivos}
      >
        {/* O cabeçalho é o PanelHeader do §8, não uma cópia: título, subtítulo em
            mono, selos à direita (slot `secondary`) e o fechar. */}
        <PanelHeader
          className='shrink-0 px-5'
          title={titulo}
          quebrarTitulo={quebrarTitulo}
          subtitle={subtitulo}
          secondary={selos ? <div className='flex items-center gap-2 flex-wrap'>{selos}</div> : undefined}
          onClose={fechavel ? onClose : undefined}
        />

        {subCabecalho && <div className='shrink-0' data-testid='casca-subcabecalho'>{subCabecalho}</div>}

        {aviso && <div className='shrink-0'>{aviso}</div>}

        {abas && abas.length > 0 && <AbasDaCasca abas={abas} abaAtiva={abaAtiva} onAba={onAba} />}

        {faixa && faixa.length > 0 && <FaixaDaCasca faixa={faixa} />}

        {topo && <div className='shrink-0'>{topo}</div>}

        <div className={`flex-1 min-h-0 overflow-y-auto ${corpoClassName}`} ref={corpoRef}>{children}</div>

        {rodape && <div className='shrink-0 flex flex-wrap items-center gap-2 px-5 py-3 border-t border-central-borda'>{rodape}</div>}
      </div>
      {perguntando && <PerguntaDeFechar onContinuar={() => setPerguntando(false)} onFechar={() => { setPerguntando(false); onClose(); }} />}
    </div>
  );
}

/**
 * As abas neutras da casca (modal e gaveta).
 *
 * MAIORES (16/10/2026, aprovado pelo Heitor para TODAS as fichas): "as abas estão muito pequenas e ruins de visualizar".
 * O tamanho da letra já era 14px; o que faltava era peso, alvo e marca. Agora: 44px de alvo de clique, 15px, peso médio
 * (semibold na ativa) e barra ativa de 3px, no molde da Central (estado é peso + barra, não só cor).
 */
export function AbasDaCasca({ abas, abaAtiva, onAba }: { abas: AbaDaCasca[]; abaAtiva?: string; onAba?: (chave: string) => void }) {
  return (
    <div className='shrink-0 flex gap-2 px-5 pt-1 bg-central-fundo/40 border-b border-central-borda' role='tablist'>
      {abas.map((a) => (
        <button
          key={a.chave}
          type='button'
          role='tab'
          aria-selected={abaAtiva === a.chave}
          onClick={() => onAba && onAba(a.chave)}
          className={`min-h-[44px] px-4 text-[15px] border-b-[3px] -mb-px transition-colors ${abaAtiva === a.chave ? 'border-central-texto text-central-texto font-semibold' : 'border-transparent text-central-texto-fraco font-medium hover:text-central-texto'}`}
        >
          {a.rotulo}
        </button>
      ))}
    </div>
  );
}

/** A faixa de números da casca (modal e gaveta). */
export function FaixaDaCasca({ faixa }: { faixa: NumeroDaFaixa[] }) {
  return (
    <div className='shrink-0 grid grid-cols-[repeat(auto-fit,minmax(100px,1fr))] gap-px bg-central-borda border-b border-central-borda'>
      {faixa.map((n) => (
        <div key={n.k} className='bg-central-cartao px-4 py-2.5' title={n.dica}>
          <div className='text-[10px] uppercase tracking-wider text-central-texto-fraco'>{n.k}</div>
          <div className={`mt-0.5 text-base font-semibold tabular-nums whitespace-nowrap ${n.cls || 'text-central-texto'}`}>{n.v}</div>
          {n.sub && <div className='text-[10px] text-central-texto-fraco truncate'>{n.sub}</div>}
        </div>
      ))}
    </div>
  );
}
